import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Copy, UserPlus, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalInvites() {
  const [invites, setInvites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDlg, setShowDlg] = useState(false);
  const [form, setForm] = useState({ email: "", client_id: "", contact_name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, c] = await Promise.all([axios.get(`${API}/portal/invites`), axios.get(`${API}/clients`)]);
      setInvites(i.data); setClients(c.data);
    } catch { toast.error("Erro"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.client_id) { toast.error("Preenche email e cliente"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/portal/invites`, form);
      const link = `${window.location.origin}${r.data.invite_link}`;
      navigator.clipboard.writeText(link);
      toast.success("Convite criado. Link copiado para o clipboard.");
      setShowDlg(false); setForm({ email: "", client_id: "", contact_name: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const copyLink = (inv) => {
    const link = `${window.location.origin}/portal/register?token=${inv.invite_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado");
  };

  return (
    <div className="space-y-4" data-testid="portal-invites-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Mail className="h-8 w-8 text-blue-600" /> Convites do Portal do Cliente</h1>
          <p className="text-sm text-muted-foreground mt-1">Gera links de convite para clientes acederem ao portal externo em <code>/portal/login</code></p>
        </div>
        <Button onClick={() => setShowDlg(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-invite-btn"><UserPlus className="h-4 w-4 mr-1" /> Novo convite</Button>
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-8" /> :
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left p-3">Cliente</th>
            <th className="text-left p-3">Email</th>
            <th className="text-left p-3">Contacto</th>
            <th className="text-left p-3">Criado</th>
            <th className="text-left p-3">Expira</th>
            <th className="text-left p-3">Estado</th>
            <th></th>
          </tr></thead>
          <tbody>
            {invites.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Sem convites</td></tr>}
            {invites.map(i => (
              <tr key={i.id} className="border-t">
                <td className="p-3 font-semibold">{i.client_name}</td>
                <td className="p-3">{i.email}</td>
                <td className="p-3 text-xs">{i.contact_name || "-"}</td>
                <td className="p-3 text-xs">{new Date(i.created_at).toLocaleDateString("pt-PT")}</td>
                <td className="p-3 text-xs">{new Date(i.expires_at).toLocaleDateString("pt-PT")}</td>
                <td className="p-3">
                  {i.used_at ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Usado</span> :
                   new Date(i.expires_at) < new Date() ? <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800">Expirado</span> :
                   <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pendente</span>}
                </td>
                <td className="p-3">
                  {!i.used_at && (
                    <Button size="sm" variant="outline" onClick={() => copyLink(i)} data-testid={`copy-invite-${i.id}`}>
                      <Copy className="h-3 w-3 mr-1" /> Copiar link
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      <Dialog open={showDlg} onOpenChange={setShowDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo convite ao portal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="invite-client"><SelectValue placeholder="Escolhe..." /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Email do contacto</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div><Label>Nome do contacto (opcional)</Label>
              <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} data-testid="invite-contact" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDlg(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} data-testid="create-invite-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Gerar convite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
